import { Button, Flex, Menu, MenuItem, Text, Tooltip } from '@electricjs/arc';
import {
  PermissionEnum,
  AssetStatus,
  AssetActionEnum,
  AssetEventActionTypes,
  EntitlementSlugEnum,
  LockStatus,
} from '@electricjs/core_entity-client';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useTranslation } from 'react-i18next';
import ArchiveAssetMenuItem from './ArchiveAssetMenuItem';
import DeleteAssetMenuItem from './DeleteAssetMenuItem';
import { useUserHasOrganizationPermission } from '@/hooks/useUserHasOrganizationPermission';
import { useEffect, useState } from 'react';
import WipeDeviceMenuItem from './WipeDeviceMenuItem';
import LockDeviceMenuItem from './LockDeviceMenuItem';
import { useGetOrganizationId } from '@/hooks/useGetOrganizationId';
import {
  useGetAssetQuery,
  useGetAssetHistoryQuery,
  useGetSiteByIdQuery,
} from '@/redux/slices/assetApiSlice';
import { getFullName } from 'common/utils/getFullName';
import useShowDeviceActions from './useShowDeviceActions';
import { AssetHistory, isDeviceAsset } from '@/types/assets';
import { useAppSelector } from '@/redux/store';
import { OrganizationUserRoleSlugs } from '@/types/users';
import UnenrollDeviceMenuItem from './UnenrollDeviceMenuItem';
import RecoveryKeyMenuItem from './RecoveryKeyMenuItem';
import ExemptFromMdmMenuItem from './ExemptFromMdmMenuItem';
import { useOrganizationHasEntitlement } from '@/hooks/useOrganizationHasEntitlement';
import { useDeviceMdmActions } from '@/hooks/useDeviceMdmActions';
import { HigherTierMDMOperationInProgress } from './LockDeviceMenuItem';
import { useNavigate } from 'react-router-dom';
import { ELECTRIC_WAREHOUSE_SITE_NAME } from '@/constants/assets';
import { SelectionParamKeys } from '@/types/queryParamKeys';
import { useGlobalUI } from '@/components/GlobalUIProvider';
import {
  LaptopAction,
  LaptopActionLink,
  getLaptopActionLink,
} from './getLaptopAction';
import DeployLaptopEmployeeModal from './DeployLaptopEmployeeModal';
import {
  useGetVisibleAssignee,
  useIsAssigneeHidden,
} from '@/hooks/useGetAssignee';

const ARCHIVED_STATUSES = new Set<AssetStatus>([
  AssetStatus.Archived,
  AssetStatus.Donated,
  AssetStatus.Sold,
  AssetStatus.Disposed,
  AssetStatus.Missing,
]);

const isAssetArchived = (status?: AssetStatus) => {
  return status ? ARCHIVED_STATUSES.has(status) : false;
};

type AssetActionsButtonMenuProps = {
  editProfile: () => void;
  assetId: string;
};

/**
 * Check if a WIPE or UNENROLL operation is currently in progress by looking at asset history
 * An operation is "in progress" if there's a "Requested" action without a corresponding "Done" action
 * This is used to disable the LOCK option if a wipe or unenroll operation is in progress
 */
const isOperationInProgress = (
  assetHistory: AssetHistory[],
  requestedAction: AssetActionEnum,
  terminalActions: AssetActionEnum[]
): boolean => {
  // Sort by creation date (most recent first)
  const sortedHistory = [...assetHistory].sort(
    (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
  );

  // Find the most recent "requested" action
  const lastRequested = sortedHistory.find(
    log => log.action === requestedAction
  );

  if (!lastRequested) {
    return false; // No operation ever requested
  }

  // Find the most recent terminal action (done, error, or timeout)
  const lastTerminal = sortedHistory.find(log =>
    terminalActions.includes(log.action as AssetActionEnum)
  );

  if (!lastTerminal) {
    return true; // Requested but never reached a terminal state
  }

  // If the requested action is more recent than the terminal action, then the operation is in progress
  return (
    new Date(lastRequested.createdAt).getTime() >
    new Date(lastTerminal.createdAt).getTime()
  );
};

export const isWipeInProgress = (assetHistory: AssetHistory[]): boolean => {
  return isOperationInProgress(
    assetHistory,
    AssetActionEnum.DeviceWipeRequested,
    [
      AssetActionEnum.DeviceWipeDone,
      AssetActionEnum.DeviceWipeTimeout,
      AssetActionEnum.DeviceWipeError,
    ]
  );
};

export const isUnenrollInProgress = (assetHistory: AssetHistory[]): boolean => {
  return isOperationInProgress(
    assetHistory,
    AssetActionEnum.DeviceUnenrollRequested,
    [AssetActionEnum.DeviceUnenrollDone, AssetActionEnum.DeviceUnenrollError]
  );
};

export const isLockErrored = (assetHistory: AssetHistory[]): boolean => {
  const sortedHistory = [...assetHistory].sort(
    (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
  );
  // Find the most recent terminal lock event — success OR error — to avoid
  // treating a historical error as active after a successful subsequent lock.
  const lastLockTerminal = sortedHistory.find(
    log =>
      log.action === AssetActionEnum.DeviceLockError ||
      log.action === AssetActionEnum.DeviceLockTimeout ||
      log.action === AssetEventActionTypes.DeviceLock
  );
  return (
    lastLockTerminal?.action === AssetActionEnum.DeviceLockError ||
    lastLockTerminal?.action === AssetActionEnum.DeviceLockTimeout
  );
};

const AssetDetailsButtonMenu = ({
  editProfile,
  assetId,
}: AssetActionsButtonMenuProps) => {
  const { t } = useTranslation('assets');
  const { ithEnableNotApplicableMdmFlagForDevices, techveraOutboundLocked } =
    useFlags();
  const [isLoading, setIsLoading] = useState(false);
  const [disableUnenrollReason, setDisableUnenrollReason] = useState<
    string | undefined
  >(undefined);
  const [disableWipeReason, setDisableWipeReason] = useState<
    string | undefined
  >(undefined);
  const canModifyAssets = useUserHasOrganizationPermission(
    PermissionEnum.Assetsupdate
  );
  const organizationId = useGetOrganizationId();
  const navigate = useNavigate();
  const { openModal, closeModal } = useGlobalUI();
  const loggedUser = useAppSelector(state => state.loggedUser);
  const isProdSupport =
    loggedUser?.organizationUserRoles?.some(
      role => role.slug === OrganizationUserRoleSlugs.PRODUCT_SUPPORT
    ) || false;

  const getAssignee = useGetVisibleAssignee();
  const isAssigneeHidden = useIsAssigneeHidden();

  const { asset, isFetchingAsset } = useGetAssetQuery(
    {
      organizationId,
      assetId,
    },
    {
      skip: !assetId,
      selectFromResult: ({ data, isFetching }) => ({
        deviceId: data?.device?.id,
        asset: data,
        isFetchingAsset: isFetching,
      }),
    }
  );

  const { data: assetLogs, isFetching: isFetchingAssetHistory } =
    useGetAssetHistoryQuery({
      organizationId,
      assetId,
      limit: 500,
    });

  const assignee = asset && getAssignee(asset);
  const employeeName = getFullName(assignee?.firstName, assignee?.lastName);

  const isFetching = isFetchingAsset || isFetchingAssetHistory;

  // The asset endpoint returns its site in the raw API shape (and not always
  // with an address), so get the site's country from the site itself
  const { data: assetSite } = useGetSiteByIdQuery(
    { siteId: asset?.site?.id ?? '', organizationId },
    { skip: !asset?.site?.id }
  );

  const lockIsInProgress =
    asset?.device?.lockStatus === LockStatus.Requested ||
    asset?.device?.lockStatus === LockStatus.Locked;

  const wipeIsInProgress = isWipeInProgress(
    assetLogs?.assetHistory?.filter(
      log =>
        log.action === AssetActionEnum.DeviceWipeRequested ||
        log.action === AssetActionEnum.DeviceWipeDone ||
        log.action === AssetActionEnum.DeviceWipeTimeout ||
        log.action === AssetActionEnum.DeviceWipeError
    ) ?? []
  );
  const unenrollIsInProgress = isUnenrollInProgress(
    assetLogs?.assetHistory.filter(
      log =>
        log.action === AssetActionEnum.DeviceUnenrollRequested ||
        log.action === AssetActionEnum.DeviceUnenrollDone ||
        log.action === AssetActionEnum.DeviceUnenrollError
    ) ?? []
  );
  const lockHasErrored = isLockErrored(
    assetLogs?.assetHistory?.filter(
      log =>
        log.action === AssetActionEnum.DeviceLockError ||
        log.action === AssetActionEnum.DeviceLockTimeout ||
        log.action === AssetEventActionTypes.DeviceLock
    ) ?? []
  );

  useEffect(() => {
    if (!isFetching) {
      /*
      MDM Actions should not be allowed if they're already in progress, or if they're product support
      Wipe — Only available if wipe is not in progress
      Unenroll — Only available if unenroll or wipe is not in progress
      Lock — Only available if lock is not in progress
      */
      if (isProdSupport) {
        const reason =
          'This feature is disabled for org switcher. Please contact Product Support if you need help';
        setDisableWipeReason(reason);
        setDisableUnenrollReason(reason);
      } else if (wipeIsInProgress) {
        const reason = 'A request to wipe this device is already in progress.';
        setDisableWipeReason(reason);
        setDisableUnenrollReason(reason);
      } else if (unenrollIsInProgress) {
        const reason =
          'A request to unenroll this device is already in progress.';
        setDisableUnenrollReason(reason);
        setDisableWipeReason(undefined);
      } else {
        setDisableWipeReason(undefined);
        setDisableUnenrollReason(undefined);
      }
    }
  }, [
    wipeIsInProgress,
    unenrollIsInProgress,
    lockIsInProgress,
    isFetching,
    isProdSupport,
  ]);

  const showDeviceActions =
    useShowDeviceActions({
      assetId,
    }) && !!asset?.device;

  const { canLock, canWipe } = useDeviceMdmActions(
    asset?.device?.mdmProviderId,
    asset?.device?.id
  );

  const lockEnabled = useUserHasOrganizationPermission(
    PermissionEnum.Deviceslock
  );
  const showLockOption = (lockEnabled || isProdSupport) && canLock !== false;
  const lockActionPending = canLock === undefined;

  const showWipeOption =
    (useUserHasOrganizationPermission(PermissionEnum.Deviceswipe) ||
      isProdSupport) &&
    canWipe !== false;
  const wipeActionPending = canWipe === undefined;

  const showUnenrollOption =
    (useUserHasOrganizationPermission(PermissionEnum.Devicesunenroll) ||
      isProdSupport) &&
    !asset?.device?.mdmProviderId;

  const showDeleteOption =
    useUserHasOrganizationPermission(PermissionEnum.Assetsdelete) &&
    isProdSupport &&
    isAssetArchived(asset?.status);

  const hasMdmEntitlement = useOrganizationHasEntitlement(
    EntitlementSlugEnum.JumpcloudMdm
  );
  const showExemptFromMdmOption =
    ithEnableNotApplicableMdmFlagForDevices &&
    canModifyAssets &&
    hasMdmEntitlement &&
    !!asset?.device;

  // Partially or totally encrypted
  const encryptionEnabled = !!asset?.device?.reportingData?.encryptionPercent;

  // Show recovery key option if device has encryption data
  const showRecoveryKeyOption = showDeviceActions && isDeviceAsset(asset);

  const laptopActionLink = getLaptopActionLink(asset, {
    siteCountry: assetSite?.address.country,
    isAssigneeHidden: !!asset && isAssigneeHidden(asset),
  });
  const showLaptopActionOption = !!laptopActionLink;

  // Same lock the storefront applies to these assets: they can't be requested while the warehouse is migrating
  const isDeployLockedByTechvera =
    techveraOutboundLocked === true &&
    laptopActionLink?.action === LaptopAction.DeployFromWarehouse &&
    asset?.site?.name === ELECTRIC_WAREHOUSE_SITE_NAME;

  const handleLaptopAction = ({ action, url }: LaptopActionLink) => {
    if (action !== LaptopAction.DeployFromWarehouse) {
      navigate(url);
      return;
    }
    // The storefront purchase is tied to an employee, so ask for one first
    openModal(
      <DeployLaptopEmployeeModal
        assetCountry={assetSite?.address.country}
        onClose={closeModal}
        onSelect={employeeId => {
          closeModal();
          navigate(`${url}&${SelectionParamKeys.EmployeeId}=${employeeId}`);
        }}
      />
    );
  };

  if (
    !canModifyAssets &&
    (!showDeviceActions ||
      (!showLockOption && !showWipeOption && !showRecoveryKeyOption)) &&
    !showDeleteOption &&
    !showLaptopActionOption &&
    !showExemptFromMdmOption
  ) {
    return null;
  }

  return (
    <Menu
      trigger={
        <Button
          id="asset-details-button-menu"
          iconAfter="chevron-down"
          loading={isLoading}>
          {t('assetDetailsButtonMenu.actions')}
        </Button>
      }
      placement="bottom-end">
      {canModifyAssets && (
        <MenuItem id="edit-asset-menu-item" onClick={editProfile}>
          <Text>{t('assetDetailsButtonMenu.editAsset')}</Text>
        </MenuItem>
      )}
      {laptopActionLink && (
        <Tooltip
          maxWidth="30rem"
          content={t('storefront:itemCard.outboundLockedTooltip')}
          placement="left"
          disabled={!isDeployLockedByTechvera}>
          <Flex width="100%">
            <MenuItem
              id="laptop-action-menu-item"
              onClick={() => handleLaptopAction(laptopActionLink)}
              disabled={isDeployLockedByTechvera}>
              <Text>
                {laptopActionLink.action === LaptopAction.Retrieve
                  ? t('assetDetailsButtonMenu.retrieveLaptop')
                  : t('assetDetailsButtonMenu.deployLaptop')}
              </Text>
            </MenuItem>
          </Flex>
        </Tooltip>
      )}
      {showDeviceActions && (
        <>
          {showWipeOption && isDeviceAsset(asset) && (
            <WipeDeviceMenuItem
              deviceAsset={asset}
              employeeName={employeeName}
              isLoading={isFetchingAsset}
              disabledBecause={
                disableWipeReason ??
                (wipeActionPending
                  ? t('assetDetailsButtonMenu.checkingMdmSupport')
                  : undefined)
              }
            />
          )}
          {showLockOption && isDeviceAsset(asset) && (
            <LockDeviceMenuItem
              deviceAsset={asset}
              encryptionEnabled={encryptionEnabled}
              employeeName={employeeName}
              isProdSupport={isProdSupport}
              lockHasErrored={lockHasErrored}
              lockActionPending={lockActionPending}
              higherTierMDMOperationInProgress={
                wipeIsInProgress
                  ? HigherTierMDMOperationInProgress.Wipe
                  : unenrollIsInProgress
                    ? HigherTierMDMOperationInProgress.Unenroll
                    : undefined
              }
            />
          )}
          {showUnenrollOption && isDeviceAsset(asset) && (
            <UnenrollDeviceMenuItem
              deviceAsset={asset}
              employeeName={employeeName}
              isAssigneeHidden={isAssigneeHidden(asset)}
              isLoading={isFetchingAsset}
              disabledBecause={disableUnenrollReason}
            />
          )}
          {showRecoveryKeyOption && isDeviceAsset(asset) && (
            <RecoveryKeyMenuItem
              deviceAsset={asset}
              isLoading={isFetchingAsset}
            />
          )}
        </>
      )}
      {canModifyAssets && (
        <ArchiveAssetMenuItem assetId={assetId} setIsloading={setIsLoading} />
      )}
      {showExemptFromMdmOption && asset && (
        <ExemptFromMdmMenuItem asset={asset} />
      )}
      {showDeleteOption && (
        <DeleteAssetMenuItem assetId={assetId} setIsloading={setIsLoading} />
      )}
    </Menu>
  );
};

export default AssetDetailsButtonMenu;
